import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import { 
  Dumbbell, 
  Clock, 
  Calendar, 
  Target, 
  ChevronRight, 
  ChevronLeft,
  Loader2,
  Download,
  CheckCircle2,
  Palmtree,
  Sparkles,
  Info,
  Lock,
  MessageCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lista de material disponible con imágenes
const MATERIAL_DISPONIBLE = [
  {
    id: 'peso_corporal',
    nombre: 'Solo mi cuerpo',
    descripcion: 'Sin equipamiento adicional',
    imagen: 'https://images.pexels.com/photos/4162489/pexels-photo-4162489.jpeg?auto=compress&w=300',
    icono: '🧍'
  },
  {
    id: 'bandas_elasticas',
    nombre: 'Bandas elásticas',
    descripcion: 'Gomas de resistencia',
    imagen: 'https://images.unsplash.com/photo-1584827386916-b5351d3ba34b?w=300',
    icono: '🔴'
  },
  {
    id: 'mancuernas_ligeras',
    nombre: 'Mancuernas ligeras',
    descripcion: '1-10 kg',
    imagen: 'https://images.pexels.com/photos/6550843/pexels-photo-6550843.jpeg?auto=compress&w=300',
    icono: '🏋️'
  },
  {
    id: 'mancuernas_pesadas',
    nombre: 'Mancuernas pesadas',
    descripcion: '10-25 kg',
    imagen: 'https://images.pexels.com/photos/4397838/pexels-photo-4397838.jpeg?auto=compress&w=300',
    icono: '💪'
  },
  {
    id: 'kettlebell',
    nombre: 'Kettlebell',
    descripcion: 'Pesa rusa',
    imagen: 'https://images.unsplash.com/photo-1632077804406-188472f1a810?w=300',
    icono: '🔔'
  },
  {
    id: 'barra_dominadas',
    nombre: 'Barra de dominadas',
    descripcion: 'Fija o de puerta',
    imagen: 'https://images.pexels.com/photos/7672109/pexels-photo-7672109.jpeg?auto=compress&w=300',
    icono: '📏'
  },
  {
    id: 'trx',
    nombre: 'TRX / Anillas',
    descripcion: 'Suspensión',
    imagen: 'https://images.unsplash.com/photo-1530021356476-0a6375ffe73b?w=300',
    icono: '⭕'
  },
  {
    id: 'banco',
    nombre: 'Banco',
    descripcion: 'Plano o inclinable',
    imagen: 'https://images.pexels.com/photos/4162438/pexels-photo-4162438.jpeg?auto=compress&w=300',
    icono: '🛋️'
  },
  {
    id: 'silla',
    nombre: 'Silla resistente',
    descripcion: 'Para fondos y step-ups',
    imagen: 'https://images.pexels.com/photos/6740749/pexels-photo-6740749.jpeg?auto=compress&w=300',
    icono: '🪑'
  },
  {
    id: 'toalla',
    nombre: 'Toalla',
    descripcion: 'Para deslizamientos',
    imagen: 'https://images.pexels.com/photos/4498294/pexels-photo-4498294.jpeg?auto=compress&w=300',
    icono: '🧺'
  }
];

// Mini-librería del módulo "Diseña tu Entreno".
// IMPORTANTE: esta librería es TOTALMENTE INDEPENDIENTE de la librería oficial GF-XXX.
// No se mezcla. No toca planes ni progreso del cliente.
// Cada ejercicio tiene { n: nombre, o: [objetivos_compatibles] }
// Objetivos: 'fuerza' | 'resistencia' | 'mantener' | 'movilidad'
const EJERCICIOS_POR_MATERIAL = {
  peso_corporal: {
    empuje: [
      { n: 'Flexiones', o: ['fuerza','mantener','resistencia'] },
      { n: 'Flexiones diamante', o: ['fuerza','mantener'] },
      { n: 'Flexiones inclinadas', o: ['mantener','resistencia'] },
      { n: 'Flexiones declinadas', o: ['fuerza','mantener'] },
      { n: 'Pike push-ups', o: ['fuerza','mantener'] },
      { n: 'Flexiones explosivas con palmada', o: ['fuerza','resistencia'] },
      { n: 'Hindu push-ups', o: ['fuerza','mantener','movilidad'] },
      { n: 'Burpees', o: ['resistencia'] },
      { n: 'Burpees sin flexión', o: ['resistencia','mantener'] },
      { n: 'Fondos en silla', o: ['fuerza','mantener'] },
      { n: 'Flexiones isométricas (3s bajada)', o: ['fuerza','mantener'] },
      { n: 'Apertura escapular en plancha', o: ['movilidad','mantener'] },
    ],
    tirón: [
      { n: 'Remo invertido en mesa', o: ['fuerza','mantener'] },
      { n: 'Superman', o: ['mantener','movilidad','resistencia'] },
      { n: 'Superman con pausa 3s', o: ['fuerza','mantener'] },
      { n: 'Facepull isométrico en pared', o: ['mantener','movilidad'] },
      { n: 'Y-T-W en suelo', o: ['movilidad','mantener'] },
      { n: 'Aducciones escapulares (puente prono)', o: ['movilidad','mantener'] },
    ],
    piernas: [
      { n: 'Sentadilla', o: ['fuerza','mantener','resistencia'] },
      { n: 'Sentadilla con pausa 3s abajo', o: ['fuerza','movilidad'] },
      { n: 'Sentadilla con salto', o: ['fuerza','resistencia'] },
      { n: 'Zancadas (lunge)', o: ['fuerza','mantener','resistencia'] },
      { n: 'Zancadas caminando', o: ['mantener','resistencia'] },
      { n: 'Zancadas con salto alternadas', o: ['fuerza','resistencia'] },
      { n: 'Sentadilla búlgara', o: ['fuerza','mantener'] },
      { n: 'Step-ups', o: ['mantener','resistencia'] },
      { n: 'Puente de glúteo', o: ['mantener','movilidad'] },
      { n: 'Puente de glúteo a una pierna', o: ['fuerza','mantener'] },
      { n: 'Sentadilla pistol asistida', o: ['fuerza','movilidad'] },
      { n: 'Sentadilla isométrica en pared 45s', o: ['fuerza','mantener'] },
      { n: 'Skater jumps (saltos laterales)', o: ['resistencia'] },
      { n: 'Elevaciones de talón sentado', o: ['mantener','movilidad'] },
      { n: 'Cossack squat', o: ['movilidad','mantener'] },
      { n: 'Sentadilla profunda con pausa', o: ['movilidad','mantener'] },
    ],
    core: [
      { n: 'Plancha 30s', o: ['mantener','resistencia'] },
      { n: 'Plancha 60s con pausa', o: ['fuerza','mantener'] },
      { n: 'Plancha lateral', o: ['fuerza','mantener'] },
      { n: 'Plancha lateral con elevación cadera', o: ['fuerza','resistencia'] },
      { n: 'Mountain climbers', o: ['resistencia'] },
      { n: 'Mountain climbers lentos cruzados', o: ['fuerza','movilidad'] },
      { n: 'Bicycle crunch', o: ['resistencia','mantener'] },
      { n: 'Dead bug', o: ['mantener','movilidad'] },
      { n: 'Hollow hold', o: ['fuerza','mantener'] },
      { n: 'Bird dog', o: ['movilidad','mantener'] },
      { n: 'Bird dog con pausa 3s', o: ['fuerza','mantener','movilidad'] },
      { n: 'Cat-cow flow', o: ['movilidad'] },
      { n: 'Rotación torácica de rodillas', o: ['movilidad'] },
      { n: 'Toe touches lentos', o: ['movilidad','mantener'] },
    ],
  },
  bandas_elasticas: {
    empuje: [
      { n: 'Press con banda pectoral', o: ['fuerza','mantener'] },
      { n: 'Press inclinado con banda', o: ['fuerza','mantener'] },
      { n: 'Aperturas con banda', o: ['mantener','movilidad'] },
      { n: 'Extensiones tríceps con banda', o: ['mantener','resistencia'] },
      { n: 'Press de hombro con banda', o: ['fuerza','mantener'] },
      { n: 'Press alternado rápido con banda', o: ['resistencia'] },
      { n: 'Banded push-ups (banda en espalda)', o: ['fuerza'] },
    ],
    tirón: [
      { n: 'Remo con banda', o: ['fuerza','mantener','resistencia'] },
      { n: 'Pulldown con banda', o: ['mantener','resistencia'] },
      { n: 'Curl bíceps con banda', o: ['mantener','resistencia'] },
      { n: 'Facepull con banda', o: ['mantener','movilidad'] },
      { n: 'Pull-apart con banda', o: ['mantener','movilidad','resistencia'] },
      { n: 'Remo isométrico 5s con banda', o: ['fuerza','mantener'] },
      { n: 'YTWL con banda', o: ['movilidad','mantener'] },
    ],
    piernas: [
      { n: 'Sentadilla con banda', o: ['fuerza','mantener'] },
      { n: 'Peso muerto con banda', o: ['fuerza','mantener'] },
      { n: 'Abductores con banda', o: ['mantener','resistencia'] },
      { n: 'Glute kickback con banda', o: ['mantener','resistencia'] },
      { n: 'Monster walks laterales con banda', o: ['resistencia','mantener'] },
      { n: 'Buenos días con banda', o: ['movilidad','mantener'] },
    ],
    core: [
      { n: 'Pallof press con banda', o: ['fuerza','mantener'] },
      { n: 'Rotaciones con banda', o: ['movilidad','resistencia'] },
      { n: 'Anti-rotación con banda 30s', o: ['fuerza','mantener'] },
    ],
  },
  mancuernas_ligeras: {
    empuje: [
      { n: 'Press de hombro con mancuernas', o: ['fuerza','mantener'] },
      { n: 'Elevaciones laterales', o: ['mantener','resistencia'] },
      { n: 'Elevaciones frontales', o: ['mantener','resistencia'] },
      { n: 'Press francés (tríceps)', o: ['fuerza','mantener'] },
      { n: 'Press Arnold', o: ['mantener','movilidad'] },
      { n: 'Elevaciones laterales lentas 4s', o: ['fuerza','mantener'] },
    ],
    tirón: [
      { n: 'Remo a una mano', o: ['fuerza','mantener'] },
      { n: 'Curl bíceps', o: ['mantener','resistencia'] },
      { n: 'Curl martillo', o: ['mantener','resistencia'] },
      { n: 'Pájaros (rear delt fly)', o: ['mantener','movilidad'] },
      { n: 'Curl bíceps 21s', o: ['resistencia'] },
      { n: 'Renegade row', o: ['fuerza'] },
    ],
    piernas: [
      { n: 'Goblet squat ligero', o: ['mantener','resistencia'] },
      { n: 'Zancadas con peso ligero', o: ['mantener','resistencia'] },
      { n: 'Peso muerto rumano ligero', o: ['mantener','movilidad'] },
      { n: 'Elevaciones de talón con peso', o: ['mantener','resistencia'] },
      { n: 'Sumo squat con mancuerna', o: ['mantener','movilidad'] },
    ],
    core: [
      { n: 'Russian twist', o: ['resistencia','mantener'] },
      { n: 'Woodchops', o: ['mantener','movilidad'] },
      { n: 'Plancha con remo (renegade)', o: ['fuerza','mantener'] },
      { n: 'Side bend con mancuerna', o: ['mantener','movilidad'] },
    ],
  },
  mancuernas_pesadas: {
    empuje: [
      { n: 'Press de pecho con mancuernas', o: ['fuerza'] },
      { n: 'Press inclinado pesado', o: ['fuerza'] },
      { n: 'Press de hombro pesado', o: ['fuerza'] },
      { n: 'Press cerrado (tríceps pesado)', o: ['fuerza'] },
      { n: 'Press isométrico arriba 10s', o: ['fuerza','mantener'] },
    ],
    tirón: [
      { n: 'Remo pesado a una mano', o: ['fuerza'] },
      { n: 'Pullover pesado', o: ['fuerza','movilidad'] },
      { n: 'Curl concentrado', o: ['fuerza','mantener'] },
      { n: 'Remo Pendlay', o: ['fuerza'] },
      { n: 'Encogimientos de hombros (shrugs)', o: ['fuerza','mantener'] },
    ],
    piernas: [
      { n: 'Peso muerto rumano pesado', o: ['fuerza'] },
      { n: 'Goblet squat pesado', o: ['fuerza'] },
      { n: 'Zancadas pesadas', o: ['fuerza'] },
      { n: 'Hip thrust con mancuerna', o: ['fuerza','mantener'] },
      { n: 'Sentadilla búlgara pesada', o: ['fuerza'] },
    ],
    core: [
      { n: 'Farmer carry 30s', o: ['fuerza','resistencia'] },
      { n: 'Suitcase carry 30s', o: ['fuerza','mantener'] },
      { n: 'Plancha con peso encima', o: ['fuerza'] },
    ],
  },
  kettlebell: {
    empuje: [
      { n: 'Press con kettlebell', o: ['fuerza','mantener'] },
      { n: 'Push press con kettlebell', o: ['fuerza','resistencia'] },
      { n: 'Bottoms-up press', o: ['fuerza','mantener'] },
      { n: 'Halo (alrededor de cabeza)', o: ['movilidad','mantener'] },
    ],
    tirón: [
      { n: 'Remo con kettlebell', o: ['fuerza','mantener'] },
      { n: 'High pull', o: ['fuerza','resistencia'] },
      { n: 'Snatch', o: ['fuerza','resistencia'] },
      { n: 'Clean estricto', o: ['fuerza'] },
    ],
    piernas: [
      { n: 'Swing ruso', o: ['resistencia','fuerza'] },
      { n: 'Goblet squat con kettlebell', o: ['fuerza','mantener'] },
      { n: 'Turkish get-up', o: ['fuerza','movilidad','mantener'] },
      { n: 'Clean and squat', o: ['fuerza','resistencia'] },
      { n: 'Sumo deadlift con kettlebell', o: ['fuerza','movilidad'] },
    ],
    core: [
      { n: 'Windmill', o: ['movilidad','mantener'] },
      { n: 'Around the world', o: ['movilidad','resistencia'] },
      { n: 'Pallof press con kettlebell', o: ['fuerza','mantener'] },
    ],
  },
  barra_dominadas: {
    empuje: [],
    tirón: [
      { n: 'Dominadas estrictas', o: ['fuerza'] },
      { n: 'Dominadas con banda asistidas', o: ['mantener','resistencia'] },
      { n: 'Chin-ups (agarre supino)', o: ['fuerza','mantener'] },
      { n: 'Dominadas negativas 5s bajada', o: ['fuerza'] },
      { n: 'Colgarse pasivo (dead hang) 30s', o: ['movilidad','mantener'] },
      { n: 'Colgarse activo escapular', o: ['movilidad','mantener'] },
      { n: 'Australian pull-ups (si barra baja)', o: ['mantener','resistencia'] },
    ],
    piernas: [],
    core: [
      { n: 'Elevaciones de piernas colgado', o: ['fuerza','mantener'] },
      { n: 'Knee raises rápidas', o: ['resistencia','mantener'] },
      { n: 'L-sit', o: ['fuerza'] },
      { n: 'Windshield wipers colgado', o: ['fuerza','movilidad'] },
    ],
  },
  trx: {
    empuje: [
      { n: 'Flexiones TRX', o: ['fuerza','mantener'] },
      { n: 'Press de pecho TRX', o: ['fuerza','mantener'] },
      { n: 'Press de hombro TRX', o: ['fuerza','mantener'] },
      { n: 'Fondos TRX', o: ['fuerza'] },
      { n: 'Flexiones TRX explosivas', o: ['fuerza','resistencia'] },
    ],
    tirón: [
      { n: 'Remo TRX', o: ['fuerza','mantener','resistencia'] },
      { n: 'Remo invertido alto TRX', o: ['fuerza','mantener'] },
      { n: 'Curl bíceps TRX', o: ['mantener','resistencia'] },
      { n: 'Facepull TRX', o: ['mantener','movilidad'] },
      { n: 'Y raises TRX', o: ['movilidad','mantener'] },
    ],
    piernas: [
      { n: 'Sentadilla TRX', o: ['mantener','resistencia'] },
      { n: 'Pistol squat asistida TRX', o: ['fuerza','movilidad'] },
      { n: 'Curl femoral TRX', o: ['fuerza','mantener'] },
      { n: 'Zancada suspendida', o: ['fuerza','mantener'] },
    ],
    core: [
      { n: 'Plancha TRX', o: ['fuerza','mantener'] },
      { n: 'Pike TRX', o: ['fuerza','movilidad'] },
      { n: 'Saw TRX', o: ['fuerza','resistencia'] },
      { n: 'Fallout TRX', o: ['fuerza','movilidad'] },
    ],
  },
  banco: {
    empuje: [
      { n: 'Flexiones con pies elevados en banco', o: ['fuerza','mantener'] },
      { n: 'Dips en banco', o: ['mantener','resistencia'] },
      { n: 'Dips en banco con lastre', o: ['fuerza'] },
    ],
    tirón: [
      { n: 'Remo apoyado en banco', o: ['fuerza','mantener'] },
    ],
    piernas: [
      { n: 'Step-ups en banco', o: ['mantener','resistencia'] },
      { n: 'Sentadilla búlgara en banco', o: ['fuerza','mantener'] },
      { n: 'Hip thrust en banco', o: ['fuerza','mantener'] },
      { n: 'Step-ups explosivos', o: ['fuerza','resistencia'] },
    ],
    core: [
      { n: 'Crunch en banco', o: ['mantener','resistencia'] },
      { n: 'Elevaciones de piernas en banco', o: ['fuerza','mantener'] },
      { n: 'Reverse hyper en banco', o: ['mantener','movilidad'] },
    ],
  },
  silla: {
    empuje: [
      { n: 'Fondos en silla', o: ['fuerza','mantener'] },
      { n: 'Flexiones inclinadas en silla', o: ['mantener','resistencia'] },
      { n: 'Pike push-ups elevadas en silla', o: ['fuerza'] },
    ],
    tirón: [],
    piernas: [
      { n: 'Step-ups en silla', o: ['mantener','resistencia'] },
      { n: 'Sentadilla búlgara en silla', o: ['fuerza','mantener'] },
      { n: 'Single leg squat to chair', o: ['fuerza','mantener'] },
      { n: 'Sentadilla a silla con pausa 3s', o: ['fuerza','movilidad'] },
    ],
    core: [
      { n: 'V-ups con silla', o: ['fuerza','resistencia'] },
      { n: 'Mountain climbers manos en silla', o: ['resistencia'] },
    ],
  },
  toalla: {
    empuje: [
      { n: 'Sliding push-ups con toalla', o: ['fuerza'] },
    ],
    tirón: [
      { n: 'Remo isométrico con toalla 20s', o: ['fuerza','mantener'] },
    ],
    piernas: [
      { n: 'Sliding lunges con toalla', o: ['fuerza','mantener'] },
      { n: 'Hamstring curl deslizante con toalla', o: ['fuerza','mantener'] },
    ],
    core: [
      { n: 'Body saw con toalla', o: ['fuerza','mantener'] },
      { n: 'Knee tuck deslizante con toalla', o: ['fuerza','resistencia'] },
      { n: 'Pike deslizante con toalla', o: ['fuerza','movilidad'] },
    ],
  },
};

const DisenaEntrenoPage = () => {
  const [paso, setPaso] = useState(1);
  const [materialSeleccionado, setMaterialSeleccionado] = useState(['peso_corporal']);
  const [tiempo, setTiempo] = useState('30');
  const [diasSemana, setDiasSemana] = useState('3');
  const [objetivo, setObjetivo] = useState('mantener');
  const [nivelExperiencia, setNivelExperiencia] = useState('intermedio');
  const [generando, setGenerando] = useState(false);
  const [rutina, setRutina] = useState(null);
  
  // Control de acceso
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [tieneAcceso, setTieneAcceso] = useState(false);

  useEffect(() => {
    checkAcceso();
  }, []);

  const checkAcceso = async () => {
    try {
      const response = await axios.get(`${API}/me/disena-entreno/acceso`);
      setTieneAcceso(response.data.tiene_acceso);
    } catch (error) {
      console.error('Error checking access:', error);
      setTieneAcceso(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const toggleMaterial = (materialId) => {
    if (materialId === 'peso_corporal') {
      // Peso corporal siempre está incluido
      return;
    }
    
    setMaterialSeleccionado(prev => {
      if (prev.includes(materialId)) {
        return prev.filter(m => m !== materialId);
      } else {
        return [...prev, materialId];
      }
    });
  };

  const generarRutina = () => {
    setGenerando(true);
    
    // Simular tiempo de generación
    setTimeout(() => {
      // Recopilar ejercicios disponibles según el material y FILTRAR por objetivo
      const ejerciciosDisponibles = {
        empuje: [],
        tirón: [],
        piernas: [],
        core: []
      };

      materialSeleccionado.forEach(material => {
        const ejercicios = EJERCICIOS_POR_MATERIAL[material];
        if (ejercicios) {
          Object.keys(ejerciciosDisponibles).forEach(categoria => {
            // Filtrar por objetivo: el ejercicio debe incluir el objetivo elegido
            const compatibles = (ejercicios[categoria] || []).filter(ex =>
              ex && Array.isArray(ex.o) && ex.o.includes(objetivo)
            );
            ejerciciosDisponibles[categoria] = [
              ...ejerciciosDisponibles[categoria],
              ...compatibles
            ];
          });
        }
      });

      // Eliminar duplicados por nombre
      Object.keys(ejerciciosDisponibles).forEach(cat => {
        const seen = new Set();
        ejerciciosDisponibles[cat] = ejerciciosDisponibles[cat].filter(ex => {
          if (seen.has(ex.n)) return false;
          seen.add(ex.n);
          return true;
        });
      });

      // Fallback: si por algún material+objetivo no quedan ejercicios suficientes en una
      // categoría, completar con ejercicios versátiles ('mantener' suele ser el más amplio).
      Object.keys(ejerciciosDisponibles).forEach(cat => {
        if (ejerciciosDisponibles[cat].length < 3) {
          materialSeleccionado.forEach(material => {
            const ejercicios = EJERCICIOS_POR_MATERIAL[material];
            const pool = (ejercicios?.[cat] || []).filter(ex =>
              ex && Array.isArray(ex.o) && ex.o.includes('mantener')
            );
            pool.forEach(ex => {
              if (!ejerciciosDisponibles[cat].some(e => e.n === ex.n)) {
                ejerciciosDisponibles[cat].push(ex);
              }
            });
          });
        }
      });

      // Shuffle pseudo-aleatorio para variabilidad entre generaciones (Fisher-Yates)
      const shuffle = (arr) => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };
      Object.keys(ejerciciosDisponibles).forEach(cat => {
        ejerciciosDisponibles[cat] = shuffle(ejerciciosDisponibles[cat]);
      });

      // Helper para tomar nombre del ejercicio (compatibilidad con structure {n, o})
      const exName = (ex) => (ex && typeof ex === 'object') ? ex.n : ex;

      // Generar rutina según días y objetivo
      const dias = parseInt(diasSemana);
      const duracion = parseInt(tiempo);
      const rutinaGenerada = [];

      // Calcular series y reps según objetivo
      const config = {
        mantener: { series: 3, reps: '10-12', descanso: '60s' },
        fuerza: { series: 4, reps: '6-8', descanso: '90s' },
        resistencia: { series: 3, reps: '15-20', descanso: '45s' },
        movilidad: { series: 2, reps: '12-15', descanso: '30s' }
      }[objetivo];

      if (dias <= 2) {
        // Full body
        for (let i = 1; i <= dias; i++) {
          const sesion = {
            dia: i,
            nombre: `Full Body ${i}`,
            tipo: 'FULL BODY',
            ejercicios: []
          };

          // Seleccionar ejercicios balanceados
          const categorias = ['empuje', 'tirón', 'piernas', 'core'];
          categorias.forEach(cat => {
            const disponibles = ejerciciosDisponibles[cat];
            if (disponibles.length > 0) {
              const numEjercicios = cat === 'piernas' ? 2 : 1;
              for (let j = 0; j < numEjercicios && j < disponibles.length; j++) {
                const idx = (i - 1 + j) % disponibles.length;
                sesion.ejercicios.push({
                  nombre: exName(disponibles[idx]),
                  series: config.series,
                  reps: config.reps,
                  categoria: cat
                });
              }
            }
          });

          rutinaGenerada.push(sesion);
        }
      } else if (dias === 3) {
        // Push/Pull/Legs
        const estructuras = [
          { nombre: 'Empuje (Push)', tipo: 'EMPUJE', categorias: ['empuje', 'core'] },
          { nombre: 'Tirón (Pull)', tipo: 'TIRÓN', categorias: ['tirón', 'core'] },
          { nombre: 'Piernas', tipo: 'PIERNAS', categorias: ['piernas', 'core'] }
        ];

        estructuras.forEach((estructura, idx) => {
          const sesion = {
            dia: idx + 1,
            nombre: estructura.nombre,
            tipo: estructura.tipo,
            ejercicios: []
          };

          estructura.categorias.forEach(cat => {
            const disponibles = ejerciciosDisponibles[cat];
            const numEjercicios = cat === 'core' ? 2 : 4;
            // Offset rotativo por sesión para que core no se repita
            const offset = cat === 'core' ? idx * 2 : 0;
            for (let j = 0; j < numEjercicios && j < disponibles.length; j++) {
              const realIdx = (offset + j) % disponibles.length;
              sesion.ejercicios.push({
                nombre: exName(disponibles[realIdx]),
                series: config.series,
                reps: config.reps,
                categoria: cat
              });
            }
          });

          rutinaGenerada.push(sesion);
        });
      } else {
        // Upper/Lower split
        const estructuras = [
          { nombre: 'Tren Superior A', tipo: 'UPPER', categorias: ['empuje', 'tirón'] },
          { nombre: 'Tren Inferior A', tipo: 'LOWER', categorias: ['piernas', 'core'] },
          { nombre: 'Tren Superior B', tipo: 'UPPER', categorias: ['empuje', 'tirón'] },
          { nombre: 'Tren Inferior B', tipo: 'LOWER', categorias: ['piernas', 'core'] }
        ];

        for (let i = 0; i < Math.min(dias, 4); i++) {
          const estructura = estructuras[i];
          const sesion = {
            dia: i + 1,
            nombre: estructura.nombre,
            tipo: estructura.tipo,
            ejercicios: []
          };

          estructura.categorias.forEach(cat => {
            const disponibles = ejerciciosDisponibles[cat];
            const offset = i >= 2 ? Math.floor(disponibles.length / 2) : 0;
            const numEjercicios = 3;
            for (let j = 0; j < numEjercicios && j < disponibles.length; j++) {
              const idx = (offset + j) % disponibles.length;
              sesion.ejercicios.push({
                nombre: exName(disponibles[idx]),
                series: config.series,
                reps: config.reps,
                categoria: cat
              });
            }
          });

          rutinaGenerada.push(sesion);
        }
      }

      setRutina({
        configuracion: {
          material: materialSeleccionado.map(m => 
            MATERIAL_DISPONIBLE.find(mat => mat.id === m)?.nombre
          ),
          tiempo: duracion,
          dias,
          objetivo,
          descanso: config.descanso
        },
        sesiones: rutinaGenerada,
        consejos: [
          objetivo === 'mantener' ? 'Mantén la tensión muscular durante todo el movimiento' : null,
          objetivo === 'fuerza' ? 'Descansa completamente entre series para máxima fuerza' : null,
          objetivo === 'resistencia' ? 'Minimiza descansos para mantener el ritmo cardíaco elevado' : null,
          'Si un ejercicio te resulta muy fácil, aumenta las repeticiones o hazlo más lento',
          'Calienta 5 minutos antes de empezar con movilidad articular',
          'Escucha a tu cuerpo: mejor hacer menos con buena técnica que más con riesgo de lesión'
        ].filter(Boolean)
      });

      setGenerando(false);
      toast.success('¡Rutina generada! 💪');
    }, 1500);
  };

  const reiniciar = () => {
    setRutina(null);
    setPaso(1);
    setMaterialSeleccionado(['peso_corporal']);
  };

  // Pantalla de carga mientras verifica acceso
  if (checkingAccess) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ClientLayout>
    );
  }

  // Pantalla de bloqueo si no tiene acceso
  if (!tieneAcceso) {
    return (
      <ClientLayout>
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 border-zinc-700">
            <CardContent className="p-8 text-center space-y-6">
              {/* Icono de bloqueo */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <Palmtree className="h-12 w-12 text-amber-500/50" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-zinc-800 rounded-full p-2 border-2 border-zinc-700">
                    <Lock className="h-6 w-6 text-amber-400" />
                  </div>
                </div>
              </div>

              {/* Título y descripción */}
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-foreground">
                  Diseña tu Entreno
                </h2>
                <p className="text-muted-foreground text-lg">
                  Esta herramienta te permite crear rutinas personalizadas cuando estás de vacaciones o sin acceso a tu gimnasio habitual.
                </p>
              </div>

              {/* Mensaje de contacto */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <MessageCircle className="h-6 w-6 text-amber-400" />
                  <span className="text-amber-400 font-semibold text-lg">Función bloqueada</span>
                </div>
                <p className="text-foreground mb-4">
                  Tienes dos formas de desbloquear esta herramienta:
                </p>
                <div className="space-y-3 text-left">
                  <div className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-sm font-bold flex items-center justify-center">1</span>
                    <div className="flex-1">
                      <p className="text-foreground font-medium text-sm">Suscríbete a Pro o Familia</p>
                      <p className="text-xs text-muted-foreground mt-0.5">14 días gratis. Cancela cuando quieras.</p>
                      <button
                        onClick={() => window.location.href = '/pricing'}
                        className="mt-2 text-primary text-sm font-semibold underline"
                        data-testid="disena-upgrade-cta"
                      >
                        Ver planes y precios →
                      </button>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-sm font-bold flex items-center justify-center">2</span>
                    <div className="flex-1">
                      <p className="text-foreground font-medium text-sm">Pide a tu entrenador que te lo active</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Tu entrenador puede darte acceso manualmente.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Características de la herramienta */}
              <div className="text-left bg-zinc-800/50 rounded-lg p-4 space-y-2">
                <p className="text-sm text-muted-foreground font-medium mb-3">¿Qué podrás hacer?</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-foreground/80">
                    <CheckCircle2 className="h-4 w-4 text-green-400/50" />
                    Seleccionar tu material
                  </div>
                  <div className="flex items-center gap-2 text-foreground/80">
                    <CheckCircle2 className="h-4 w-4 text-green-400/50" />
                    Elegir tiempo disponible
                  </div>
                  <div className="flex items-center gap-2 text-foreground/80">
                    <CheckCircle2 className="h-4 w-4 text-green-400/50" />
                    Definir tu objetivo
                  </div>
                  <div className="flex items-center gap-2 text-foreground/80">
                    <CheckCircle2 className="h-4 w-4 text-green-400/50" />
                    Generar rutina automática
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header */}
        <Card className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500/20 rounded-xl">
                <Palmtree className="h-8 w-8 text-amber-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Diseña tu Entreno</h1>
                <p className="text-muted-foreground">
                  ¿De vacaciones? ¿Sin gym? Crea tu rutina con lo que tengas disponible.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {!rutina ? (
          <>
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3].map((p) => (
                <div
                  key={p}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    p === paso ? 'bg-amber-500' : p < paso ? 'bg-amber-500/50' : 'bg-zinc-700'
                  }`}
                />
              ))}
            </div>

            {/* PASO 1: Selección de material */}
            {paso === 1 && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Dumbbell className="h-5 w-5 text-amber-400" />
                    Paso 1: ¿Qué material tienes?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Selecciona TODO el equipamiento disponible donde estés.
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {MATERIAL_DISPONIBLE.map((material) => {
                      const isSelected = materialSeleccionado.includes(material.id);
                      const isBase = material.id === 'peso_corporal';
                      
                      return (
                        <div
                          key={material.id}
                          onClick={() => toggleMaterial(material.id)}
                          className={`relative cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                            isSelected
                              ? 'border-amber-500 bg-amber-500/10'
                              : 'border-zinc-700 hover:border-zinc-600'
                          } ${isBase ? 'ring-2 ring-amber-500/30' : ''}`}
                        >
                          <div className="aspect-square relative">
                            <img
                              src={material.imagen}
                              alt={material.nombre}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://images.pexels.com/photos/4162489/pexels-photo-4162489.jpeg?auto=compress&w=300';
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                            
                            {isSelected && (
                              <div className="absolute top-2 right-2">
                                <CheckCircle2 className="h-6 w-6 text-amber-400" />
                              </div>
                            )}
                            
                            {isBase && (
                              <div className="absolute top-2 left-2 px-2 py-0.5 bg-amber-500/80 rounded text-xs font-medium">
                                Base
                              </div>
                            )}
                          </div>
                          
                          <div className="absolute bottom-0 left-0 right-0 p-2">
                            <p className="font-medium text-white text-sm truncate">
                              {material.icono} {material.nombre}
                            </p>
                            <p className="text-xs text-zinc-300 truncate">
                              {material.descripcion}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={() => setPaso(2)}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* PASO 2: Configuración */}
            {paso === 2 && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-400" />
                    Paso 2: Configura tu rutina
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Tiempo */}
                  <div className="space-y-3">
                    <Label className="text-foreground">¿Cuánto tiempo tienes por sesión?</Label>
                    <RadioGroup value={tiempo} onValueChange={setTiempo} className="grid grid-cols-4 gap-3">
                      {[
                        { value: '15', label: '15 min', desc: 'Express' },
                        { value: '30', label: '30 min', desc: 'Corta' },
                        { value: '45', label: '45 min', desc: 'Media' },
                        { value: '60', label: '60 min', desc: 'Completa' }
                      ].map((opt) => (
                        <Label
                          key={opt.value}
                          className={`flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            tiempo === opt.value
                              ? 'border-amber-500 bg-amber-500/10'
                              : 'border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <RadioGroupItem value={opt.value} className="sr-only" />
                          <span className="text-lg font-bold text-foreground">{opt.label}</span>
                          <span className="text-xs text-muted-foreground">{opt.desc}</span>
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>

                  {/* Días */}
                  <div className="space-y-3">
                    <Label className="text-foreground">¿Cuántos días a la semana?</Label>
                    <RadioGroup value={diasSemana} onValueChange={setDiasSemana} className="grid grid-cols-4 gap-3">
                      {[
                        { value: '2', label: '2 días', desc: 'Full Body' },
                        { value: '3', label: '3 días', desc: 'Push/Pull/Legs' },
                        { value: '4', label: '4 días', desc: 'Upper/Lower' },
                        { value: '5', label: '5+ días', desc: 'Avanzado' }
                      ].map((opt) => (
                        <Label
                          key={opt.value}
                          className={`flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            diasSemana === opt.value
                              ? 'border-amber-500 bg-amber-500/10'
                              : 'border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <RadioGroupItem value={opt.value} className="sr-only" />
                          <span className="text-lg font-bold text-foreground">{opt.label}</span>
                          <span className="text-xs text-muted-foreground">{opt.desc}</span>
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={() => setPaso(1)}>
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Atrás
                    </Button>
                    <Button
                      onClick={() => setPaso(3)}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* PASO 3: Objetivo */}
            {paso === 3 && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-amber-400" />
                    Paso 3: ¿Cuál es tu objetivo?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <RadioGroup value={objetivo} onValueChange={setObjetivo} className="grid grid-cols-2 gap-4">
                    {[
                      { value: 'mantener', label: 'Mantener músculo', desc: 'No perder lo ganado', emoji: '💪' },
                      { value: 'fuerza', label: 'No perder fuerza', desc: 'Series pesadas, pocas reps', emoji: '🏋️' },
                      { value: 'resistencia', label: 'Quemar calorías', desc: 'Más reps, menos descanso', emoji: '🔥' },
                      { value: 'movilidad', label: 'Movilidad', desc: 'Recuperación activa', emoji: '🧘' }
                    ].map((opt) => (
                      <Label
                        key={opt.value}
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          objetivo === opt.value
                            ? 'border-amber-500 bg-amber-500/10'
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <RadioGroupItem value={opt.value} className="sr-only" />
                        <span className="text-3xl">{opt.emoji}</span>
                        <div>
                          <p className="font-semibold text-foreground">{opt.label}</p>
                          <p className="text-sm text-muted-foreground">{opt.desc}</p>
                        </div>
                      </Label>
                    ))}
                  </RadioGroup>

                  {/* Nivel de experiencia */}
                  <div className="space-y-3">
                    <Label className="text-foreground">Tu nivel de experiencia</Label>
                    <RadioGroup value={nivelExperiencia} onValueChange={setNivelExperiencia} className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'principiante', label: 'Principiante', desc: '<1 año' },
                        { value: 'intermedio', label: 'Intermedio', desc: '1-3 años' },
                        { value: 'avanzado', label: 'Avanzado', desc: '3+ años' }
                      ].map((opt) => (
                        <Label
                          key={opt.value}
                          className={`flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            nivelExperiencia === opt.value
                              ? 'border-amber-500 bg-amber-500/10'
                              : 'border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <RadioGroupItem value={opt.value} className="sr-only" />
                          <span className="font-bold text-foreground">{opt.label}</span>
                          <span className="text-xs text-muted-foreground">{opt.desc}</span>
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={() => setPaso(2)}>
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Atrás
                    </Button>
                    <Button
                      onClick={generarRutina}
                      disabled={generando}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {generando ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generar Rutina
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* RUTINA GENERADA */
          <div className="space-y-4">
            {/* Resumen de configuración */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Dumbbell className="h-4 w-4 text-amber-400" />
                    <span className="text-muted-foreground">Material:</span>
                    <span className="text-foreground">{rutina.configuracion.material.join(', ')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-400" />
                    <span className="text-foreground">{rutina.configuracion.tiempo} min</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-amber-400" />
                    <span className="text-foreground">{rutina.configuracion.dias} días/semana</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-amber-400" />
                    <span className="text-foreground capitalize">{rutina.configuracion.objetivo}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sesiones */}
            {rutina.sesiones.map((sesion, idx) => (
              <Card key={idx} className="bg-card border-border">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center text-amber-400 font-bold">
                        {sesion.dia}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{sesion.nombre}</CardTitle>
                        <p className="text-xs text-amber-400">{sesion.tipo}</p>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Descanso: {rutina.configuracion.descanso}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="space-y-2">
                    {sesion.ejercicios.map((ejercicio, eIdx) => (
                      <div
                        key={eIdx}
                        className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 bg-zinc-700 rounded flex items-center justify-center text-xs text-muted-foreground">
                            {eIdx + 1}
                          </span>
                          <div>
                            <p className="font-medium text-foreground">{ejercicio.nombre}</p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {ejercicio.categoria}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-amber-400">
                            {ejercicio.series} × {ejercicio.reps}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Consejos */}
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-semibold text-foreground mb-2">Consejos</p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {rutina.consejos.map((consejo, idx) => (
                        <li key={idx}>• {consejo}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Acciones */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={reiniciar} className="flex-1">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Nueva rutina
              </Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-700">
                <Download className="h-4 w-4 mr-2" />
                Guardar PDF
              </Button>
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default DisenaEntrenoPage;
